import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { BlockFormSelectReasonForHardBlock } from '@eika-kortbetjening/block-form-select-reason-for-hard-block';

import SkeletonList from '@/components/skeleton/SkeletonList';
import piwik from '@/piwik/piwik';
import { useCard } from '@/react-query/hooks/query/use-card';

import { useLinkProducer, useNavigate } from '@domain/app';
import { HardBlockReason } from '@generated-types';
import useText from '@hooks/use-text';
import { canHardBlock } from '@utils/allowed-operations/card-allowed-operations-utils';
import { DEBIT_HARD_BLOCK_REASONS } from '@utils/block-reorder-utils';
import { isHardBlocked } from '@utils/card-status-utils';
import { isDebit } from '@utils/card-type-utils';
import { ensureStringIsReason } from '@utils/query-utils';
import { getCardDetailsUrl, navigateToCardDetails } from '@utils/url/get-card-details-url-utils';
import { navigateToHardBlockConfirm, navigateToReorder } from '@utils/url/get-url-utils';

const SelectReason = () => {
    const text = useText();
    const { data: card, isLoading } = useCard();
    const navigate = useNavigate();
    const linkProducer = useLinkProducer();

    const [selectedReason, setSelectedReason] = useState<HardBlockReason | undefined>(undefined);
    const [formError, setFormError] = useState<string | undefined>(undefined);

    const optionSelected = useMemo(() => typeof selectedReason !== 'undefined', [selectedReason]);

    useEffect(() => {
        if (optionSelected) {
            setFormError(undefined);
        }
    }, [optionSelected]);

    if (typeof card === 'undefined' || isLoading) {
        return <SkeletonList />;
    }

    if (isHardBlocked(card) || !canHardBlock(card)) {
        return <Navigate to={getCardDetailsUrl(linkProducer)(card)} replace />;
    }

    const next = () => {
        if (typeof selectedReason === 'undefined') {
            setFormError(text.messageErrorOptionRequired);
            return;
        }

        if (selectedReason === HardBlockReason.NOT_IN_USE) {
            navigateToHardBlockConfirm(navigate)({ card, reorder: false, selectedReason });
            return;
        }

        if (selectedReason === HardBlockReason.DAMAGED && isDebit(card)) {
            navigateToHardBlockConfirm(navigate)({ card, reorder: true, selectedReason });
            return;
        }

        if (selectedReason === HardBlockReason.STOLEN || selectedReason === HardBlockReason.LOST) {
            navigateToReorder(navigate)(card, selectedReason);

            return;
        }

        navigateToHardBlockConfirm(navigate)({ card, reorder: false, selectedReason });
    };

    const cancel = () => {
        navigateToCardDetails(navigate)(card);
    };

    const onChange = (value: string | undefined) => {
        setSelectedReason(ensureStringIsReason(value) ?? undefined);
    };

    return (
        <BlockFormSelectReasonForHardBlock
            possibleReasons={DEBIT_HARD_BLOCK_REASONS}
            selectedReason={selectedReason}
            setSelectedReason={onChange}
            selectedReasonError={formError}
            next={next}
            cancel={cancel}
            sitecoreDictionary={text}
            piwik={piwik}
        />
    );
};

export default SelectReason;
